import { useState } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Switch } from '@/components/ui/switch';
import { Separator } from '@/components/ui/separator';
import { Badge } from '@/components/ui/badge';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '@/components/ui/dialog';
import {
  Star,
  MessageSquareQuote,
  Shield,
  ImageIcon,
  Plus,
  Trash2,
  Pencil,
  Upload,
  GripVertical,
} from 'lucide-react';
import type {
  WidgetConfig,
  WidgetReview,
  TrustBadge,
  TrustBadgeType,
  BeforeAfterProject,
} from '@/types/firestore';

interface SocialProofSettingsProps {
  widgetConfig: WidgetConfig;
  onConfigChange: (config: WidgetConfig) => void;
}

const DEFAULT_TRUST_BADGES: TrustBadge[] = [
  { type: 'bbb', enabled: false, label: 'BBB Accredited' },
  { type: 'licensed', enabled: false, label: 'Licensed' },
  { type: 'insured', enabled: false, label: 'Fully Insured' },
  { type: 'bonded', enabled: false, label: 'Bonded' },
];

export function SocialProofSettings({ widgetConfig, onConfigChange }: SocialProofSettingsProps) {
  const [newReviewOpen, setNewReviewOpen] = useState(false);
  const [editReviewIndex, setEditReviewIndex] = useState<number | null>(null);
  const [newProjectOpen, setNewProjectOpen] = useState(false);
  const [editProjectIndex, setEditProjectIndex] = useState<number | null>(null);

  // Review form state
  const [reviewText, setReviewText] = useState('');
  const [reviewerName, setReviewerName] = useState('');
  const [reviewDate, setReviewDate] = useState('');
  const [reviewRating, setReviewRating] = useState(5);

  // Project form state
  const [projectBeforeUrl, setProjectBeforeUrl] = useState('');
  const [projectAfterUrl, setProjectAfterUrl] = useState('');
  const [projectType, setProjectType] = useState('');
  const [projectDescription, setProjectDescription] = useState('');

  const reviews = widgetConfig.reviews || [];
  const trustBadges = widgetConfig.trust_badges || DEFAULT_TRUST_BADGES;
  const projects = widgetConfig.before_after_projects || [];

  // Review handlers
  const resetReviewForm = () => {
    setReviewText('');
    setReviewerName('');
    setReviewDate('');
    setReviewRating(5);
  };

  const openEditReview = (index: number) => {
    const review = reviews[index];
    setReviewText(review.text);
    setReviewerName(review.reviewer_name);
    setReviewDate(review.date || '');
    setReviewRating(review.rating ?? 5);
    setEditReviewIndex(index);
  };

  const handleSaveReview = () => {
    const newReview: WidgetReview = {
      text: reviewText,
      reviewer_name: reviewerName,
      date: reviewDate || undefined,
      rating: reviewRating,
    };

    let updatedReviews: WidgetReview[];
    if (editReviewIndex !== null) {
      updatedReviews = [...reviews];
      updatedReviews[editReviewIndex] = newReview;
      setEditReviewIndex(null);
    } else {
      updatedReviews = [...reviews, newReview];
      setNewReviewOpen(false);
    }

    onConfigChange({ ...widgetConfig, reviews: updatedReviews });
    resetReviewForm();
  };

  const handleDeleteReview = (index: number) => {
    const updatedReviews = reviews.filter((_, i) => i !== index);
    onConfigChange({ ...widgetConfig, reviews: updatedReviews });
  };

  // Trust badge handlers
  const handleToggleBadge = (type: TrustBadgeType, enabled: boolean) => {
    const updatedBadges = trustBadges.map((badge) =>
      badge.type === type ? { ...badge, enabled } : badge
    );
    onConfigChange({ ...widgetConfig, trust_badges: updatedBadges });
  };

  // Project handlers
  const resetProjectForm = () => {
    setProjectBeforeUrl('');
    setProjectAfterUrl('');
    setProjectType('');
    setProjectDescription('');
  };

  const openEditProject = (index: number) => {
    const project = projects[index];
    setProjectBeforeUrl(project.before_url);
    setProjectAfterUrl(project.after_url);
    setProjectType(project.project_type);
    setProjectDescription(project.description || '');
    setEditProjectIndex(index);
  };

  const handleSaveProject = () => {
    const newProject: BeforeAfterProject = {
      before_url: projectBeforeUrl,
      after_url: projectAfterUrl,
      project_type: projectType,
      description: projectDescription || undefined,
    };

    let updatedProjects: BeforeAfterProject[];
    if (editProjectIndex !== null) {
      updatedProjects = [...projects];
      updatedProjects[editProjectIndex] = newProject;
      setEditProjectIndex(null);
    } else {
      updatedProjects = [...projects, newProject];
      setNewProjectOpen(false);
    }

    onConfigChange({ ...widgetConfig, before_after_projects: updatedProjects });
    resetProjectForm();
  };

  const handleDeleteProject = (index: number) => {
    const updatedProjects = projects.filter((_, i) => i !== index);
    onConfigChange({ ...widgetConfig, before_after_projects: updatedProjects });
  };

  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <MessageSquareQuote className="h-5 w-5" />
          Social Proof
        </CardTitle>
        <CardDescription>
          Display reviews, trust badges, and past projects during the processing step
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-6">
        {/* Customer Reviews Section */}
        <div className="space-y-4">
          <div className="flex items-center justify-between">
            <div>
              <Label className="text-base font-medium">Customer Reviews</Label>
              <p className="text-sm text-muted-foreground">
                Testimonials shown while design is generating ({reviews.length}/5)
              </p>
            </div>
            <Dialog open={newReviewOpen} onOpenChange={setNewReviewOpen}>
              <DialogTrigger asChild>
                <Button
                  size="sm"
                  variant="outline"
                  disabled={reviews.length >= 5}
                  onClick={resetReviewForm}
                >
                  <Plus className="mr-2 h-4 w-4" />
                  Add Review
                </Button>
              </DialogTrigger>
              <DialogContent>
                <DialogHeader>
                  <DialogTitle>Add Customer Review</DialogTitle>
                  <DialogDescription>
                    Add a testimonial to show during the processing step
                  </DialogDescription>
                </DialogHeader>
                <div className="space-y-4 py-4">
                  <div className="space-y-2">
                    <Label htmlFor="review-text">Review Text *</Label>
                    <Textarea
                      id="review-text"
                      value={reviewText}
                      onChange={(e) => setReviewText(e.target.value)}
                      placeholder="They transformed our kitchen beyond our expectations..."
                      maxLength={300}
                    />
                    <p className="text-xs text-muted-foreground">
                      {reviewText.length}/300 characters
                    </p>
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="reviewer-name">Reviewer Name *</Label>
                    <Input
                      id="reviewer-name"
                      value={reviewerName}
                      onChange={(e) => setReviewerName(e.target.value)}
                      placeholder="John D."
                    />
                  </div>
                  <div className="grid grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <Label htmlFor="review-date">Date (optional)</Label>
                      <Input
                        id="review-date"
                        value={reviewDate}
                        onChange={(e) => setReviewDate(e.target.value)}
                        placeholder="January 2024"
                      />
                    </div>
                    <div className="space-y-2">
                      <Label>Rating</Label>
                      <div className="flex gap-1">
                        {[1, 2, 3, 4, 5].map((star) => (
                          <button
                            key={star}
                            type="button"
                            onClick={() => setReviewRating(star)}
                            className="p-1"
                          >
                            <Star
                              className={`h-5 w-5 ${
                                star <= reviewRating
                                  ? 'fill-yellow-400 text-yellow-400'
                                  : 'text-muted-foreground/30'
                              }`}
                            />
                          </button>
                        ))}
                      </div>
                    </div>
                  </div>
                </div>
                <DialogFooter>
                  <Button
                    onClick={handleSaveReview}
                    disabled={!reviewText.trim() || !reviewerName.trim()}
                  >
                    Add Review
                  </Button>
                </DialogFooter>
              </DialogContent>
            </Dialog>
          </div>

          {/* Reviews List */}
          {reviews.length > 0 ? (
            <div className="space-y-2">
              {reviews.map((review, index) => (
                <div
                  key={index}
                  className="flex items-start gap-3 p-3 rounded-lg border border-border/50 bg-muted/20"
                >
                  <GripVertical className="h-5 w-5 text-muted-foreground mt-0.5 cursor-grab" />
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 mb-1">
                      <span className="font-medium text-sm">{review.reviewer_name}</span>
                      {review.date && (
                        <span className="text-xs text-muted-foreground">{review.date}</span>
                      )}
                      <div className="flex gap-0.5">
                        {[1, 2, 3, 4, 5].map((star) => (
                          <Star
                            key={star}
                            className={`h-3 w-3 ${
                              star <= (review.rating ?? 5)
                                ? 'fill-yellow-400 text-yellow-400'
                                : 'text-muted-foreground/30'
                            }`}
                          />
                        ))}
                      </div>
                    </div>
                    <p className="text-sm text-muted-foreground line-clamp-2">"{review.text}"</p>
                  </div>
                  <div className="flex gap-1">
                    <Button
                      size="icon"
                      variant="ghost"
                      className="h-8 w-8"
                      onClick={() => openEditReview(index)}
                    >
                      <Pencil className="h-4 w-4" />
                    </Button>
                    <Button
                      size="icon"
                      variant="ghost"
                      className="h-8 w-8 text-destructive hover:text-destructive"
                      onClick={() => handleDeleteReview(index)}
                    >
                      <Trash2 className="h-4 w-4" />
                    </Button>
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <div className="text-center py-6 border-2 border-dashed border-muted rounded-lg">
              <MessageSquareQuote className="h-8 w-8 text-muted-foreground mx-auto mb-2" />
              <p className="text-sm text-muted-foreground">No reviews added yet</p>
              <p className="text-xs text-muted-foreground">
                Add customer testimonials to build trust during processing
              </p>
            </div>
          )}

          {/* Edit Review Dialog */}
          <Dialog
            open={editReviewIndex !== null}
            onOpenChange={(open) => !open && setEditReviewIndex(null)}
          >
            <DialogContent>
              <DialogHeader>
                <DialogTitle>Edit Review</DialogTitle>
              </DialogHeader>
              <div className="space-y-4 py-4">
                <div className="space-y-2">
                  <Label htmlFor="edit-review-text">Review Text *</Label>
                  <Textarea
                    id="edit-review-text"
                    value={reviewText}
                    onChange={(e) => setReviewText(e.target.value)}
                    maxLength={300}
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="edit-reviewer-name">Reviewer Name *</Label>
                  <Input
                    id="edit-reviewer-name"
                    value={reviewerName}
                    onChange={(e) => setReviewerName(e.target.value)}
                  />
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label htmlFor="edit-review-date">Date</Label>
                    <Input
                      id="edit-review-date"
                      value={reviewDate}
                      onChange={(e) => setReviewDate(e.target.value)}
                    />
                  </div>
                  <div className="space-y-2">
                    <Label>Rating</Label>
                    <div className="flex gap-1">
                      {[1, 2, 3, 4, 5].map((star) => (
                        <button
                          key={star}
                          type="button"
                          onClick={() => setReviewRating(star)}
                          className="p-1"
                        >
                          <Star
                            className={`h-5 w-5 ${
                              star <= reviewRating
                                ? 'fill-yellow-400 text-yellow-400'
                                : 'text-muted-foreground/30'
                            }`}
                          />
                        </button>
                      ))}
                    </div>
                  </div>
                </div>
              </div>
              <DialogFooter>
                <Button
                  onClick={handleSaveReview}
                  disabled={!reviewText.trim() || !reviewerName.trim()}
                >
                  Save Changes
                </Button>
              </DialogFooter>
            </DialogContent>
          </Dialog>
        </div>

        <Separator />

        {/* Trust Badges Section */}
        <div className="space-y-4">
          <div>
            <Label className="text-base font-medium">Trust Badges</Label>
            <p className="text-sm text-muted-foreground">
              Display certification and accreditation badges
            </p>
          </div>

          <div className="grid grid-cols-2 gap-3">
            {trustBadges.map((badge) => (
              <div
                key={badge.type}
                className="flex items-center justify-between p-3 rounded-lg border border-border/50"
              >
                <div className="flex items-center gap-3">
                  <Shield className="h-5 w-5 text-muted-foreground" />
                  <span className="text-sm font-medium">{badge.label || badge.type}</span>
                </div>
                <Switch
                  checked={badge.enabled}
                  onCheckedChange={(checked) => handleToggleBadge(badge.type, checked)}
                />
              </div>
            ))}
          </div>
        </div>

        <Separator />

        {/* Before/After Gallery Section */}
        <div className="space-y-4">
          <div className="flex items-center justify-between">
            <div>
              <Label className="text-base font-medium">Before/After Gallery</Label>
              <p className="text-sm text-muted-foreground">
                Showcase past projects ({projects.length}/6)
              </p>
            </div>
            <Dialog open={newProjectOpen} onOpenChange={setNewProjectOpen}>
              <DialogTrigger asChild>
                <Button
                  size="sm"
                  variant="outline"
                  disabled={projects.length >= 6}
                  onClick={resetProjectForm}
                >
                  <Plus className="mr-2 h-4 w-4" />
                  Add Project
                </Button>
              </DialogTrigger>
              <DialogContent>
                <DialogHeader>
                  <DialogTitle>Add Before/After Project</DialogTitle>
                  <DialogDescription>
                    Add images of a completed project to showcase your work
                  </DialogDescription>
                </DialogHeader>
                <div className="space-y-4 py-4">
                  <div className="space-y-2">
                    <Label htmlFor="project-type">Project Type *</Label>
                    <Input
                      id="project-type"
                      value={projectType}
                      onChange={(e) => setProjectType(e.target.value)}
                      placeholder="Kitchen Remodel"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="before-url">Before Image URL *</Label>
                    <div className="flex gap-2">
                      <Input
                        id="before-url"
                        value={projectBeforeUrl}
                        onChange={(e) => setProjectBeforeUrl(e.target.value)}
                        placeholder="https://..."
                      />
                    </div>
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="after-url">After Image URL *</Label>
                    <div className="flex gap-2">
                      <Input
                        id="after-url"
                        value={projectAfterUrl}
                        onChange={(e) => setProjectAfterUrl(e.target.value)}
                        placeholder="https://..."
                      />
                    </div>
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="project-description">Description (optional)</Label>
                    <Textarea
                      id="project-description"
                      value={projectDescription}
                      onChange={(e) => setProjectDescription(e.target.value)}
                      placeholder="Complete kitchen transformation with custom cabinets..."
                      maxLength={150}
                    />
                  </div>
                </div>
                <DialogFooter>
                  <Button
                    onClick={handleSaveProject}
                    disabled={
                      !projectType.trim() || !projectBeforeUrl.trim() || !projectAfterUrl.trim()
                    }
                  >
                    Add Project
                  </Button>
                </DialogFooter>
              </DialogContent>
            </Dialog>
          </div>

          {/* Projects Grid */}
          {projects.length > 0 ? (
            <div className="grid grid-cols-2 gap-3">
              {projects.map((project, index) => (
                <div
                  key={index}
                  className="relative group rounded-lg border border-border/50 overflow-hidden"
                >
                  <div className="aspect-video bg-muted relative">
                    <img
                      src={project.after_url}
                      alt={project.project_type}
                      className="w-full h-full object-cover"
                    />
                    <Badge
                      variant="secondary"
                      className="absolute top-2 left-2 bg-background/90 backdrop-blur-sm"
                    >
                      {project.project_type}
                    </Badge>
                  </div>
                  <div className="absolute inset-0 bg-background/80 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center gap-2">
                    <Button size="icon" variant="secondary" onClick={() => openEditProject(index)}>
                      <Pencil className="h-4 w-4" />
                    </Button>
                    <Button
                      size="icon"
                      variant="destructive"
                      onClick={() => handleDeleteProject(index)}
                    >
                      <Trash2 className="h-4 w-4" />
                    </Button>
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <div className="text-center py-6 border-2 border-dashed border-muted rounded-lg">
              <ImageIcon className="h-8 w-8 text-muted-foreground mx-auto mb-2" />
              <p className="text-sm text-muted-foreground">No projects added yet</p>
              <p className="text-xs text-muted-foreground">
                Add before/after images to showcase your work
              </p>
            </div>
          )}

          {/* Edit Project Dialog */}
          <Dialog
            open={editProjectIndex !== null}
            onOpenChange={(open) => !open && setEditProjectIndex(null)}
          >
            <DialogContent>
              <DialogHeader>
                <DialogTitle>Edit Project</DialogTitle>
              </DialogHeader>
              <div className="space-y-4 py-4">
                <div className="space-y-2">
                  <Label htmlFor="edit-project-type">Project Type *</Label>
                  <Input
                    id="edit-project-type"
                    value={projectType}
                    onChange={(e) => setProjectType(e.target.value)}
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="edit-before-url">Before Image URL *</Label>
                  <Input
                    id="edit-before-url"
                    value={projectBeforeUrl}
                    onChange={(e) => setProjectBeforeUrl(e.target.value)}
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="edit-after-url">After Image URL *</Label>
                  <Input
                    id="edit-after-url"
                    value={projectAfterUrl}
                    onChange={(e) => setProjectAfterUrl(e.target.value)}
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="edit-project-description">Description</Label>
                  <Textarea
                    id="edit-project-description"
                    value={projectDescription}
                    onChange={(e) => setProjectDescription(e.target.value)}
                    maxLength={150}
                  />
                </div>
              </div>
              <DialogFooter>
                <Button
                  onClick={handleSaveProject}
                  disabled={
                    !projectType.trim() || !projectBeforeUrl.trim() || !projectAfterUrl.trim()
                  }
                >
                  Save Changes
                </Button>
              </DialogFooter>
            </DialogContent>
          </Dialog>
        </div>
      </CardContent>
    </Card>
  );
}
